import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/MediaKit.css';

const BRAND_COLORS = [
  { name: 'Forest Green', hex: '#074e3b', usage: 'Logo background, primary brand' },
  { name: 'V Mark Gray', hex: '#e2e8ef', usage: 'Logo mark, light accents' },
  { name: 'Gold', hex: '#c9a961', usage: 'Premium features, badges' },
  { name: 'Cream', hex: '#faf8f3', usage: 'Page background' },
  { name: 'Gem Purple', hex: '#6a1b9a', usage: '"Gem" status' },
  { name: 'Recommended Green', hex: '#38a169', usage: '"Highly Recommended" status' },
  { name: 'Want to Go Amber', hex: '#ffc107', usage: '"Want to Go" status' },
];

const LOGO_ASSETS = [
  { name: 'Icon (Green)', file: 'vibary-icon-green', description: 'Primary app icon, white V on green background', formats: ['png', 'svg'], darkBg: false },
  { name: 'Icon (Rounded)', file: 'vibary-icon-rounded', description: 'iOS-style rounded app icon', formats: ['png', 'svg'], darkBg: false },
  { name: 'Icon (Black)', file: 'vibary-icon-black', description: 'White V on black, for dark contexts', formats: ['png', 'svg'], darkBg: false },
  { name: 'Mark (Green)', file: 'vibary-mark-green', description: 'Green V mark on transparent background', formats: ['png', 'svg'], darkBg: false },
  { name: 'Mark (Light)', file: 'vibary-mark-light', description: 'Light V mark for dark backgrounds', formats: ['png', 'svg'], darkBg: true },
  { name: 'Wordmark (Green)', file: 'vibary-wordmark-green', description: 'Full "Vibary" wordmark in green', formats: ['png', 'svg'], darkBg: false, wordmark: true },
  { name: 'Wordmark (Light)', file: 'vibary-wordmark-light', description: 'Light wordmark for dark backgrounds', formats: ['png', 'svg'], darkBg: true, wordmark: true },
];

function ColorSwatch({ color }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(color.hex);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isLight = ['#faf8f3', '#e2e8ef', '#ffc107'].includes(color.hex);

  return (
    <div className="mediakit-color-swatch" onClick={handleCopy}>
      <div
        className="mediakit-color-preview"
        style={{ backgroundColor: color.hex }}
      >
        <span className={`mediakit-color-hex ${isLight ? 'dark-text' : ''}`}>
          {copied ? 'Copied!' : color.hex}
        </span>
      </div>
      <div className="mediakit-color-info">
        <span className="mediakit-color-name">{color.name}</span>
        <span className="mediakit-color-usage">{color.usage}</span>
      </div>
    </div>
  );
}

function LogoAsset({ asset }) {
  const handleDownload = (format) => {
    const link = document.createElement('a');
    link.href = `/${asset.file}.${format}`;
    link.download = `${asset.file}.${format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const previewClasses = [
    'mediakit-logo-preview',
    asset.darkBg ? 'dark-bg' : '',
    asset.wordmark ? 'wordmark' : ''
  ].filter(Boolean).join(' ');

  return (
    <div className="mediakit-logo-card">
      <div className={previewClasses}>
        <img src={`/${asset.file}.png`} alt={asset.name} />
      </div>
      <div className="mediakit-logo-info">
        <span className="mediakit-logo-name">{asset.name}</span>
        <span className="mediakit-logo-description">{asset.description}</span>
      </div>
      <div className="mediakit-download-buttons">
        {asset.formats.map((format) => (
          <button
            key={format}
            className="mediakit-download-btn"
            onClick={() => handleDownload(format)}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            {format.toUpperCase()}
          </button>
        ))}
      </div>
    </div>
  );
}

function MediaKit() {
  const navigate = useNavigate();

  return (
    <div className="mediakit-container">
      {/* Header */}
      <header className="mediakit-header">
        <div className="mediakit-header-content">
          <button className="mediakit-back-btn" onClick={() => navigate(-1)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Back
          </button>
          <div className="mediakit-header-brand">
            <img src="/vibary-icon-rounded.png" alt="Vibary" className="mediakit-header-logo" />
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="mediakit-hero">
        <h1>Media Kit</h1>
        <p>Brand assets, guidelines, and resources for press and partners</p>
      </section>

      {/* Quick Nav - sticky below hero */}
      <nav className="mediakit-nav">
        <a href="#about">About</a>
        <a href="#logos">Logos</a>
        <a href="#colors">Colors</a>
        <a href="#typography">Typography</a>
        <a href="#voice">Brand Voice</a>
        <a href="#social">Social</a>
        <a href="#contact">Contact</a>
      </nav>

      {/* About Section */}
      <section id="about" className="mediakit-section mediakit-section-alt">
        <div className="mediakit-section-header">
          <h2>About Vibary</h2>
        </div>
        <div className="mediakit-about-card">
          <div className="mediakit-tagline">
            <span className="mediakit-tagline-label">Tagline</span>
            <h3>"Your personal library of vibes"</h3>
          </div>
          <p className="mediakit-about-description">
            Vibary is a place discovery and curation app that helps users save, organize, and share their favorite places with people they trust. We're transforming how people discover and share place recommendations, moving beyond fragmented lists in notes apps and DMs to a beautiful, organized, and shareable experience.
          </p>
          <div className="mediakit-value-props">
            <div className="mediakit-value-prop">
              <strong>Curate Your World</strong>
              <span>Create personalized lists of restaurants, shops, museums, and more</span>
            </div>
            <div className="mediakit-value-prop">
              <strong>Discover with Trust</strong>
              <span>Find recommendations from people you actually trust</span>
            </div>
            <div className="mediakit-value-prop">
              <strong>Never Forget a Gem</strong>
              <span>Track visited spots, want-to-gos, and all-time favorites</span>
            </div>
            <div className="mediakit-value-prop">
              <strong>Explore Visually</strong>
              <span>See all your saved places on an interactive map</span>
            </div>
          </div>
        </div>
      </section>

      {/* Logo Section */}
      <section id="logos" className="mediakit-section">
        <div className="mediakit-section-header">
          <h2>Logo Assets</h2>
          <p>Download our logo in various formats and sizes</p>
        </div>
        <div className="mediakit-logo-grid">
          {LOGO_ASSETS.map((asset, index) => (
            <LogoAsset key={index} asset={asset} />
          ))}
        </div>
        <div className="mediakit-logo-guidelines">
          <h4>Logo Usage Guidelines</h4>
          <ul>
            <li>Maintain clear space around the logo equal to the height of the "V" letterform</li>
            <li>Minimum digital size: 32px height</li>
            <li>Never alter, distort, or recolor the logo</li>
            <li>Use the white/light version on dark backgrounds</li>
          </ul>
        </div>
      </section>

      {/* Color Palette Section */}
      <section id="colors" className="mediakit-section mediakit-section-alt">
        <div className="mediakit-section-header">
          <h2>Color Palette</h2>
          <p>Click any color to copy the hex code</p>
        </div>
        <div className="mediakit-colors-grid">
          {BRAND_COLORS.map((color, index) => (
            <ColorSwatch key={index} color={color} />
          ))}
        </div>
      </section>

      {/* Typography Section */}
      <section id="typography" className="mediakit-section">
        <div className="mediakit-section-header">
          <h2>Typography</h2>
        </div>
        <div className="mediakit-typography-grid">
          <div className="mediakit-typography-card">
            <h4>Headings</h4>
            <p className="mediakit-font-name">Outfit</p>
            <p className="mediakit-font-sample" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 600 }}>
              The quick brown fox jumps over the lazy dog
            </p>
            <span className="mediakit-font-usage">Weight: 600-700 • Used for titles, headers, emphasis</span>
          </div>
          <div className="mediakit-typography-card">
            <h4>Body</h4>
            <p className="mediakit-font-name">Inter</p>
            <p className="mediakit-font-sample" style={{ fontFamily: 'Inter, sans-serif' }}>
              The quick brown fox jumps over the lazy dog
            </p>
            <span className="mediakit-font-usage">Weight: 400-600 • Used for body text, descriptions, UI elements</span>
          </div>
        </div>
      </section>

      {/* Brand Voice Section */}
      <section id="voice" className="mediakit-section mediakit-section-alt">
        <div className="mediakit-section-header">
          <h2>Brand Voice</h2>
        </div>
        <div className="mediakit-voice-grid">
          <div className="mediakit-voice-card mediakit-voice-do">
            <h4>Do</h4>
            <ul>
              <li>Authentic, personal, curated</li>
              <li>Sophisticated but accessible</li>
              <li>Discovery-focused</li>
              <li>Trust and community driven</li>
            </ul>
          </div>
          <div className="mediakit-voice-card mediakit-voice-dont">
            <h4>Don't</h4>
            <ul>
              <li>Generic, mass-market, impersonal</li>
              <li>Pretentious or exclusive</li>
              <li>Just another list app</li>
              <li>Discount anyone's voice</li>
            </ul>
          </div>
        </div>
        <div className="mediakit-tone-card">
          <h4>Tone</h4>
          <div className="mediakit-tone-items">
            <span className="mediakit-tone-item"><strong>Warm</strong> but not overly casual</span>
            <span className="mediakit-tone-item"><strong>Sophisticated</strong> but not stuffy</span>
            <span className="mediakit-tone-item"><strong>Helpful</strong> and actionable</span>
            <span className="mediakit-tone-item"><strong>Visual</strong> and aesthetically conscious</span>
          </div>
        </div>
      </section>

      {/* Social Media Section */}
      <section id="social" className="mediakit-section">
        <div className="mediakit-section-header">
          <h2>Social Media</h2>
        </div>
        <div className="mediakit-social-grid">
          <div className="mediakit-social-card">
            <h4>Handles</h4>
            <ul>
              <li><strong>Instagram:</strong> @getvibary</li>
              <li><strong>Twitter/X:</strong> @getvibary</li>
              <li><strong>BlueSky:</strong> @getvibary</li>
            </ul>
          </div>
          <div className="mediakit-social-card">
            <h4>Hashtags</h4>
            <div className="mediakit-hashtags">
              <span className="mediakit-hashtag">#Vibary</span>
              <span className="mediakit-hashtag">#VibaryList</span>
              <span className="mediakit-hashtag">#VibaryFinds</span>
	      <span className="mediakit-hashtag">#FoundOnVibary</span>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="mediakit-section mediakit-section-alt mediakit-section-contact">
        <div className="mediakit-section-header">
          <h2>Contact</h2>
        </div>
        <div className="mediakit-contact-grid">
          <a href="mailto:support@getvibary.com" className="mediakit-contact-card">
            <span className="mediakit-contact-label">Creator Partnerships</span>
            <span className="mediakit-contact-email">support@getvibary.com</span>
          </a>
          <a href="mailto:support@getvibary.com" className="mediakit-contact-card">
            <span className="mediakit-contact-label">Press Inquiries</span>
            <span className="mediakit-contact-email">support@getvibary.com</span>
          </a>
          <a href="mailto:support@getvibary.com" className="mediakit-contact-card">
            <span className="mediakit-contact-label">General Support</span>
            <span className="mediakit-contact-email">support@getvibary.com</span>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="mediakit-footer">
        <div className="mediakit-footer-content">
          <div className="mediakit-footer-brand">
            <img src="/vibary-wordmark-light.png" alt="Vibary" className="mediakit-footer-logo" />
          </div>
          <p>© {new Date().getFullYear()} Vibary. All rights reserved.</p>
          <p className="mediakit-footer-note">
            Assets provided are licensed for use in approved partnership content only.
            Modification of logos, colors, or other brand elements is not permitted without written approval.
          </p>
          <div className="mediakit-footer-links">
            <a href="/">Home</a>
            <a href="/creator">Creator Program</a>
            <a href="/legal/privacy">Privacy Policy</a>
            <a href="/legal/terms">Terms of Service</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default MediaKit;
